import type { LeadObject, LeadObjectInput } from '../api/types';

// Objekt eines Leads (OFFENE_FRAGEN B-20): Bezeichnungen und Hilfen für die Anzeige.

export type ObjectField = keyof LeadObjectInput;

// Felder, die es nur am Objekt gibt – in Anzeigereihenfolge, gruppiert.
export const unitFields: [ObjectField, string, string][] = [
  ['unit_number', 'Einheit laut Teilungserklärung', 'z. B. WE 5'],
  ['unit_location', 'Lage im Haus', 'z. B. 2. OG links'],
  ['ownership_share', 'Miteigentumsanteil', 'z. B. 125/1000'],
];

export const registerFields: [ObjectField, string, string][] = [
  ['land_registry_court', 'Amtsgericht', 'z. B. Böblingen'],
  ['land_register_district', 'Grundbuch von', 'z. B. Sindelfingen'],
  ['land_register_sheet', 'Blatt', 'z. B. 4711'],
  ['cadastral_district', 'Gemarkung', 'z. B. Sindelfingen'],
  ['cadastral_section', 'Flur', 'z. B. 3'],
  ['parcel', 'Flurstück', 'z. B. 1234/5'],
];

const de = (n: number) => n.toLocaleString('de-DE');

// Verkaufszeitraum: Werte der Gesprächsauswertung; Angaben der Quelle (Freitext) erscheinen unverändert.
export const timeframeLabels: Record<string, string> = {
  IMMEDIATE: 'sofort',
  WITHIN_3_MONTHS: 'in 3 Monaten',
  WITHIN_6_MONTHS: 'in 6 Monaten',
  WITHIN_12_MONTHS: 'in 12 Monaten',
  LATER: 'später',
};

// saleTimeframeText: „Termin innerhalb 14 Tagen“ setzt leados-lite bei jedem Portal-Lead fest – keine Angabe des Eigentümers.
export function saleTimeframeText(v?: string | null): string {
  if (v && timeframeLabels[v]) return timeframeLabels[v];
  const t = (v ?? '').replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
  if (!t || /^termin innerhalb (von )?14 tagen$/i.test(t)) return '';
  return t;
}

// objectFacts: Merkmale des Objekts als Paare (nur vorhandene).
export function objectFacts(o: LeadObject): [string, string][] {
  const out: [string, string][] = [];
  if (o.property_subtype) out.push(['Art', o.property_subtype]);
  if (o.living_area_sqm) out.push(['Wohnfläche', `${de(o.living_area_sqm)} m²`]);
  if (o.plot_area_sqm) out.push(['Grundstück', `${de(o.plot_area_sqm)} m²`]);
  if (o.rooms) out.push(['Zimmer', de(o.rooms)]);
  if (o.year_built) out.push(['Baujahr', String(o.year_built)]);
  if (o.price_expectation_eur) out.push(['Preisvorstellung', `${de(o.price_expectation_eur)} €`]);
  const sale = saleTimeframeText(o.sale_timeframe);
  if (sale) out.push(['Verkauf', sale]);
  return out;
}

// filled: Felder einer Gruppe, die am Objekt gesetzt sind.
export function filled(o: LeadObject, fields: [ObjectField, string, string][]): [string, string][] {
  return fields.flatMap(([k, label]) => {
    const v = o[k];
    return typeof v === 'string' && v.trim() ? [[label, v] as [string, string]] : [];
  });
}
